import { useEffect, useRef, useState } from "react"
import Button from "@ui/button/Button"
import { toast } from "react-toastify"
import { systemErrorDetails, systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { parsePositiveAmountToMinorUnits } from "src/utils/amounts"
import { isCardBlockingError } from "src/utils/cardStatus"
import { formatMinorCurrency } from "src/utils/systemBalance"
import { AdminModal } from "./AdminModal"
import { Callout, Check, DangerZone, Disclosure, Fact, Facts, Fields, Muted, Row, State, StatusBadge } from "./adminUi"

type Destination = "buyer" | "company"
export interface BalanceReturnPreview {
	apply: boolean
	card: { uid: string; status: string; mask: string; owner: { uid: string; name: string; company_role: string }; team: { uid: string; name: string } | null }
	issuer_label: string
	destination: Destination
	currency: string
	balances: { withdraw_sees_cents: number | null; withdraw_sees_error: string | null; issuer_cents: number | null; issuer_error: string | null; trx_cents: number | null }
	residual_cents: number
	amount_cents: number
	withdrawal?: Withdrawal
}
interface Withdrawal {
	id: number; status: string; destination: Destination; card_uid: string; order_id: string; amount: number; currency: string
	created_at: string; withdrawal_id: string; trx_id: number; trx_hash: string
}

const STATUS_HINT: Record<string, string> = {
	created: "The issuer is still processing it.",
	started: "The issuer is still processing it.",
	completed: "Done.",
	declined: "The issuer refused. The funds were taken back from the recipient and stay on the card.",
	canceled: "The issuer refused. The funds were taken back from the recipient and stay on the card.",
	error: "The issuer refused. The funds were taken back from the recipient and stay on the card."
}

/** Empty amount = return everything the issuer allows. */
const parseAmount = (value: string): { amount?: number; error?: string } => value.trim() ? parsePositiveAmountToMinorUnits(value) : {}
const PREVIEW_DEBOUNCE_MS = 400

/** Funds go to the buyer only when the owner is still a buyer with a team; otherwise the backend sends them to the company. */
const goesToCompany = (preview: BalanceReturnPreview) =>
	preview.destination === "company" || !preview.card.team || preview.card.owner.company_role !== "buyer"

/** `blocking`: cards/block is running — the issuer withdraws the balance itself, so a manual return is hidden until it finishes. */
export const CardBalanceReturn = ({ token, cardUid, blocking, onReturned }: { token: string; cardUid: string; blocking?: boolean; onReturned: () => void }) => {
	const [open, setOpen] = useState(false)
	const [last, setLast] = useState<Withdrawal | null>(null)
	const [refreshing, setRefreshing] = useState(false)
	const [refreshError, setRefreshError] = useState("")

	const refreshStatus = async () => {
		if (!last) return
		setRefreshing(true)
		setRefreshError("")
		const response = await systemPost<{ withdrawals?: Array<{ id: number; status: string }> }>(token, "system/cards/topups-withdrawals", { card_uid: cardUid })
		setRefreshing(false)
		if (response.error) { setRefreshError(systemErrorDetails(response.error)); return }
		const row = response.data.withdrawals?.find((item) => item.id === last.id)
		if (row) setLast({ ...last, status: row.status })
		else setRefreshError(`Return #${last.id} is not in the card's withdrawals yet.`)
	}

	return <DangerZone title='Return card balance' hint='Moves real funds from the card to the buyer or the company.'>
		<Muted style={{ fontSize: 13 }}>Works for blocked cards too. You will see exactly what moves before confirming.</Muted>
		{blocking
			? <Callout tone='warn' role='status'>The card is being blocked: the issuer withdraws the balance as part of the block. Returning it by hand is unavailable until the block finishes.</Callout>
			: <Row><Button variant='dangerSmall' onClick={() => setOpen(true)}>Return balance from card…</Button></Row>}
		{last && <Callout tone={last.status === "completed" ? "success" : ["declined", "canceled", "error"].includes(last.status) ? "danger" : "info"} role='status' title={`Return #${last.id}: ${formatMinorCurrency(last.amount, last.currency)} to the ${last.destination}`}>
			<Row><StatusBadge value={last.status} /><span>{STATUS_HINT[last.status] ?? ""}</span><Button variant='secondarySmall' onClick={() => void refreshStatus()} disabled={refreshing}>{refreshing ? "Refreshing…" : "Refresh"}</Button></Row>
			{refreshError && <span>{refreshError}</span>}
			<Disclosure flat title='Technical details'>
				<Facts>
					<Fact label='Order ID'><code>{last.order_id}</code></Fact>
					<Fact label='Issuer withdrawal ID'><code>{last.withdrawal_id}</code></Fact>
					<Fact label='TRX'><code>{last.trx_id} · {last.trx_hash}</code></Fact>
					<Fact label='Started'>{last.created_at}</Fact>
				</Facts>
			</Disclosure>
		</Callout>}
		{open && !blocking && <ReturnModal token={token} cardUid={cardUid} onClose={() => setOpen(false)} onBlocked={(message) => {
			setOpen(false)
			toast.error(message)
			onReturned()
		}} onDone={(withdrawal) => {
			setOpen(false)
			setLast(withdrawal)
			toast.success(`Return of ${formatMinorCurrency(withdrawal.amount, withdrawal.currency)} started`)
			onReturned()
		}} />}
	</DangerZone>
}

const ReturnModal = ({ token, cardUid, onClose, onBlocked, onDone }: { token: string; cardUid: string; onClose: () => void; onBlocked: (message: string) => void; onDone: (withdrawal: Withdrawal) => void }) => {
	const [amount, setAmount] = useState("")
	const [destination, setDestination] = useState<Destination>("buyer")
	const [preview, setPreview] = useState<{ key: string; data: BalanceReturnPreview } | null>(null)
	const [previewError, setPreviewError] = useState("")
	const [previewing, setPreviewing] = useState(false)
	const [confirmed, setConfirmed] = useState(false)
	const [applyError, setApplyError] = useState("")
	const [applying, setApplying] = useState(false)
	const [round, setRound] = useState(0)
	const generation = useRef(0)
	const busy = useRef(false)

	const parsed = parseAmount(amount)
	const cents = parsed.amount
	const key = `${parsed.error ? "invalid" : cents ?? "all"}|${destination}`
	const body = { card_uid: cardUid, amount: cents, destination }

	// Preview on open and after every change of amount or recipient; it moves nothing and is not journaled.
	useEffect(() => {
		const current = ++generation.current
		setPreviewError("")
		if (parsed.error) { setPreviewing(false); return }
		setPreviewing(true)
		const timer = setTimeout(async () => {
			const response = await systemPost<BalanceReturnPreview>(token, "system/cards/balance/return", body)
			if (current !== generation.current) return
			setPreviewing(false)
			if (response.error) { setPreview(null); setPreviewError(systemErrorDetails(response.error)); return }
			setPreview({ key, data: response.data })
		}, amount ? PREVIEW_DEBOUNCE_MS : 0)
		return () => clearTimeout(timer)
	}, [key, round]) // eslint-disable-line react-hooks/exhaustive-deps

	const data = preview?.key === key && !previewing ? preview.data : null
	const balanceError = data?.balances.withdraw_sees_error || data?.balances.issuer_error
	const money = (value: number | null) => formatMinorCurrency(value, data?.currency ?? "USD")
	const ready = Boolean(data && !balanceError && data.amount_cents > 0 && confirmed && !applying)

	const apply = async () => {
		// Every apply creates a new withdrawal: one request at a time, and a fresh preview after each attempt.
		if (busy.current || !ready) return
		busy.current = true
		setApplying(true)
		setApplyError("")
		const response = await systemPost<BalanceReturnPreview>(token, "system/cards/balance/return", { ...body, apply: true })
		busy.current = false
		setApplying(false)
		setPreview(null)
		// 2501: a block started after the card was loaded; reload it so it comes back with `blocking`.
		if (response.error && isCardBlockingError(response.error)) { onBlocked(systemErrorMessage(response.error)); return }
		if (response.error || !response.data.withdrawal) {
			setApplyError(response.error ? systemErrorDetails(response.error) : "The backend did not confirm the withdrawal.")
			setRound((value) => value + 1)
			return
		}
		onDone(response.data.withdrawal)
	}

	return <AdminModal title='Return card balance' subtitle='Moves real funds. Check the preview before confirming.' width={600} onClose={() => { if (!busy.current) onClose() }}>
		<div style={{ display: "grid", gap: 14, padding: 20 }}>
			<Fields>
				<label>Amount, USD<input inputMode='decimal' placeholder='Everything available' value={amount} onChange={(event) => setAmount(event.target.value)} disabled={applying} aria-invalid={Boolean(parsed.error) || undefined} /></label>
				<label>Recipient<select value={destination} onChange={(event) => setDestination(event.target.value as Destination)} disabled={applying}>
					<option value='buyer'>Card owner's balance in their team</option>
					<option value='company'>Company balance</option>
				</select></label>
			</Fields>
			{parsed.error && <Callout tone='danger'>{parsed.error} Leave it empty to return everything.</Callout>}
			{previewing && <State loading />}
			{previewError && <Callout tone='danger'>{previewError}</Callout>}
			{data && <>
				<Callout tone='info' role='status'>
					<span>From card <b>{data.card.mask}</b> ({data.issuer_label}) {goesToCompany(data)
						? <>to the <b>company balance</b></>
						: <>to buyer <b>{data.card.owner.name}</b>, team <b>{data.card.team?.name}</b></>} will be moved <b>{money(data.amount_cents)}</b>.</span>
					{destination === "buyer" && goesToCompany(data) && <span>The owner has no team or is no longer a buyer, so the funds go to the company.</span>}
					<span>On the card now: at the issuer {money(data.balances.issuer_cents)}, in TRX {money(data.balances.trx_cents)}.{data.residual_cents > 0 && ` The issuer keeps ${money(data.residual_cents)} on the card.`}</span>
				</Callout>
				{data.balances.withdraw_sees_error && <Callout tone='danger'>{data.balances.withdraw_sees_error}</Callout>}
				{data.balances.issuer_error && <Callout tone='danger'>{data.balances.issuer_error}</Callout>}
			</>}
			{applyError && <Callout tone='danger'>{applyError}</Callout>}
			<Check checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} disabled={applying}>I understand the funds will be debited from the card</Check>
			<Row style={{ justifyContent: "flex-end" }}>
				<Button variant='secondarySmall' onClick={onClose} disabled={applying}>Cancel</Button>
				<Button variant='dangerSmall' onClick={() => void apply()} disabled={!ready}>{applying ? "Returning…" : data ? `Return ${money(data.amount_cents)}` : "Return"}</Button>
			</Row>
		</div>
	</AdminModal>
}
